import { BOOKED_LAST_30, PATIENTS_IN_PROGRESS, activeRows, recallStore } from './store';
import type { RecallCampaignsData } from './types';

/** Recall campaigns demo (boards 17–20). Alamo Heights Dental. */
export const RECALL_CAMPAIGNS: RecallCampaignsData = {
  rows: recallStore.rows,
  patientsInProgress: PATIENTS_IN_PROGRESS,
  bookedLast30: BOOKED_LAST_30,
  split: [
    { was: 'Appointment confirmation', now: 'Appointment Reminders', note: 'The steps follow the lead time, rather than a fixed list.' },
    { was: 'Pre-visit preparation', now: 'Appointment Reminders', note: 'Same agent, same visit — that is how the patient experiences it.' },
    { was: 'Post-op check', now: 'Appointment Reminders', note: 'The only thing ELVA sends that may say urgent.' },
    { was: 'The review ask', now: 'Appointment Reminders', note: 'Deferred by a clinical problem, never suppressed by one.' },
    { was: 'Treatment plan follow-up', now: 'Treatment Coordinator', note: 'With a decay policy and a backlog Recall never had.' },
    { was: 'Outstanding balance', now: 'Accounts receivable', note: 'It was texting patients about money the insurer owed. Now there is an eligibility gate.' },
  ],
  audience: {
    describePlaceholder: 'Who should this recall go to?',
    describeDefault:
      'Patients the PMS says are due for hygiene, who have been in within two years and are not already booked.',
    starters: ['Due for hygiene', 'Not been in for 18 months', 'Turning 65 this year', 'Has unused benefit'],
    chips: [
      { label: 'Hygiene due — from the PMS', fromPms: true },
      { label: 'Last visit within 24 months' },
      { label: 'No future appointment booked' },
      { label: 'Active patient' },
    ],
    matched: 412,
    contactable: 387,
    exclusions: [
      { count: 11, reason: 'at their contact limit' },
      { count: 7, reason: 'opted out of this topic' },
      { count: 4, reason: 'have no mobile or email' },
      { count: 3, reason: 'were messaged by another agent today' },
    ],
    people: [
      { name: 'Marguerite Oyelowo', meta: '54 · Dr. Saito', why: 'Hygiene due from the PMS · last in 6 months ago', lastVisit: '14 Mar', due: 'Nov', status: 'will', statusLabel: 'Will be contacted' },
      { name: 'Devon Achebe', meta: '31 · Dr. Chen', why: 'Hygiene due · last visit was a filling on #19', lastVisit: '22 Mar', due: 'Nov', status: 'will', statusLabel: 'Will be contacted' },
      { name: 'Ruth Sandoval', meta: '67 · hygiene', why: 'Hygiene due · ignored two texts last cycle', lastVisit: '2 Apr', due: 'Dec', status: 'will', statusLabel: 'Will be contacted' },
      { name: 'Callum Brightwater', meta: '28 · Dr. Okafor', why: 'Hygiene due from the PMS', lastVisit: '9 Apr', due: 'Dec', status: 'limit', statusLabel: 'At their contact limit' },
      { name: 'Ines Vasconcelos', meta: '45 · Dr. Saito', why: 'Hygiene due from the PMS', lastVisit: '17 Apr', due: 'Dec', status: 'opted-out', statusLabel: 'Opted out of recall' },
      { name: 'Theo Lindqvist', meta: '39 · hygiene', why: 'Hygiene due · new patient last spring', lastVisit: '24 Apr', due: 'Dec', status: 'will', statusLabel: 'Will be contacted' },
    ],
    peopleShownOf: 412,
  },
  sequence: {
    name: 'Hygiene — 6 month',
    contactable: 387,
    steps: [
      { key: 's1', n: '1', day: 'day 0', channel: 'Text', mode: 'hard', summary: 'Your cleaning is due. One tap to book.' },
      {
        key: 's2',
        n: '2',
        day: 'day 5',
        channel: 'Email',
        mode: 'smart',
        summary: 'Remind them their cleaning is due.',
        open: true,
        brief:
          'Remind them their cleaning is due and make booking feel like one small thing. Warm, short, no pressure. If they had a specific procedure last time, name it instead of saying “your last cleaning”.',
        mustMention: ['The month they are due', 'The practice name', 'A way to book'],
        previews: [
          { name: 'Marguerite Oyelowo', meta: 'due November', body: 'Hi Marguerite — you’re due for a cleaning in November at Alamo Heights Dental. Want me to find you a time?' },
          { name: 'Devon Achebe', meta: 'due November · last visit was a filling on #19', body: 'Hi Devon — it’s been six months since we sorted that filling on #19, so you’re due for a cleaning in November. Shall I book you in?' },
          { name: 'Ruth Sandoval', meta: 'due December · ignored two texts last cycle', body: 'Hi Ruth — a quick one: your cleaning is due in December. One tap and it’s done, or ignore this and I’ll leave it.' },
        ],
      },
      { key: 's3', n: '3', day: 'day 12', channel: 'AI call', mode: 'smart', summary: 'Inside the call window. Books on the call if they want it.' },
      { key: 's4', n: '4', day: 'day 21', channel: 'Human call', mode: 'none', summary: 'A task in Triage. ELVA never pushes a daily call list — staff choose them.' },
    ],
    editVariant: {
      title: 'What this change will do',
      lines: [
        'You rewrote step 2’s content. It reaches all 412, including the 324 already part-way through — the next person due a step 2 gets the new words.',
        'You removed step 4, the human call. That applies to the 88 who have not started.',
      ],
      note:
        'Content changes are immediate; shape changes are not. Content is what you fix because it is wrong, so the fix should reach whoever has not been messaged yet. Inserting or removing a step mid-flight would send somebody on step 4 a step 2.',
    },
  },
  report: {
    name: 'Hygiene — 6 month',
    cycle: 'One cycle · 1 Mar → 30 Sep 2026 · goal: Book',
    state: 'active',
    heroProduction: '$22,100',
    heroSub:
      'from 119 visits · production, not collections · $186 average · 31% of the audience booked, 29% came in',
    funnel: [
      { key: 'audience', label: 'Audience', value: 412, note: 'Everyone the query matched' },
      { key: 'contacted', label: 'Contacted', value: 387, pct: '94%', note: '25 never contacted — cap, consent, no details' },
      { key: 'engaged', label: 'Engaged', value: 198, pct: '48%', note: 'Clicked, replied, or answered the phone', emphasis: true },
      { key: 'booked', label: 'Booked', value: 128, pct: '31%', note: 'An appointment on the book' },
      { key: 'attended', label: 'Attended', value: 119, pct: '29%', note: 'They came in. 9 did not', emphasis: true },
    ],
    steps: [
      { n: '1', day: 'day 0', channel: 'Text', sent: 387, reached: 381, booked: 61, ofReached: '16%', wait: '48% of every booking in this cycle came from the first text alone. If only one step survived, it is this one.' },
      { n: '2', day: 'day 5', channel: 'Email', sent: 296, reached: 288, booked: 28, ofReached: '10%', wait: 'The email picks up 28 more — people who never opened the text.' },
      { n: '3', day: 'day 12', channel: 'AI call', sent: 241, reached: 87, booked: 26, ofReached: '30%', wait: 'Reaches a third of who it dials — but a third of them book.' },
      { n: '4', day: 'day 21', channel: 'Human call', sent: 187, reached: 35, booked: 13, ofReached: '37%', wait: '13 appointments from 35 conversations. Whether that is worth the hours is a judgement the practice makes; this row is the evidence.' },
    ],
    conclusion:
      'The human call converts best of anything here, and it is the only step that needs a person. 37% of the people it reaches book — against 30% for the AI call and 16% for the text. It also reaches the fewest: 35 of the 187 it was raised for, because somebody has to pick up the phone.',
    bases: [
      { label: 'Booking rate', value: '31%', note: '128 booked ÷ 412 in the audience — not ÷ contacted' },
      { label: 'Attendance', value: '93%', note: '119 attended ÷ 128 booked' },
      { label: 'Messages sent', value: '1,111', note: 'the four steps added together. Not patients — a patient may get four' },
      { label: 'Production', value: '$22,100', note: '119 visits × $186 average. This campaign only' },
      { label: 'Reached', value: 'varies by step', note: 'delivered for text and email; answered for a call — never the same as sent' },
    ],
  },
};

/**
 * The link strip on Settings › Recall reads these, so it cannot disagree with the
 * list — which was the complaint: the settings page said 6 while the list said
 * something else. **One query, two screens.**
 */
export function recallCounts(): { total: number; running: number } {
  return { total: recallStore.rows.length, running: activeRows().length };
}

